import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';
import { LogOut, Edit2, Copy, Check, Users, Plus, Gift, Flame, Lock, Trophy, Zap, Shield, Crown, Star } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const AVATARS = ['🧑‍🎓', '👩‍🎓', '🦸', '🦸‍♀️', '🧙', '🧙‍♀️', '🦊', '🐼', '🦁', '🐯', '🦉', '🐸'];

const BADGE_ICONS = {
  fire: Flame, crown: Crown, trophy: Trophy, zap: Zap, shield: Shield, star: Star, diamond: Star,
};
const BADGE_COLORS = {
  fire: '#FF6B35', crown: '#FFD700', trophy: '#FFD23F', zap: '#8338EC', shield: '#1B4F8A', star: '#06D6A0', diamond: '#B9F2FF',
};

export default function StudentProfile() {
  const { user, profile, subscription, token, API, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [badgesData, setBadgesData] = useState({ badges: [], total_earned: 0, current_streak: 0, coins: {} });
  const [streakData, setStreakData] = useState({ milestones: [], streak: {}, current_tier: null, next_tier: null });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', avatar_url: '', school_name: '', city: '' });
  const [copied, setCopied] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joiningClass, setJoiningClass] = useState(false);
  const [myClasses, setMyClasses] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setForm({
      name: user?.name || '', avatar_url: user?.avatar_url || '🧑‍🎓',
      school_name: profile?.school_name || '', city: profile?.city || ''
    });
    const fetchAll = async () => {
      try {
        const [badgeRes, streakRes, classRes] = await Promise.all([
          axios.get(`${API}/student/my-badges`, { headers, withCredentials: true }),
          axios.get(`${API}/student/streak-rewards`, { headers, withCredentials: true }),
          axios.get(`${API}/student/my-classes`, { headers, withCredentials: true }),
        ]);
        setBadgesData(badgeRes.data);
        setStreakData(streakRes.data);
        setMyClasses(classRes.data.classes || []);
      } catch {}
    };
    if (token) fetchAll();
  }, [user, profile, API, token]);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/student/profile`, form, {
        headers: { ...headers, 'Content-Type': 'application/json' }, withCredentials: true
      });
      await refreshUser();
      setEditing(false);
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update profile'); }
  };

  const handleLogout = async () => { await logout(); navigate('/', { replace: true }); };

  const handleJoinClass = async () => {
    if (!joinCode.trim()) { toast.error('Enter a class code'); return; }
    setJoiningClass(true);
    try {
      const res = await axios.post(`${API}/teacher/class/join`, { join_code: joinCode.trim() }, {
        headers: { ...headers, 'Content-Type': 'application/json' }, withCredentials: true
      });
      toast.success(res.data.message || 'Joined class!');
      setJoinCode('');
      const clsRes = await axios.get(`${API}/student/my-classes`, { headers, withCredentials: true });
      setMyClasses(clsRes.data.classes || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to join class'); }
    finally { setJoiningClass(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(profile?.student_code || '');
    setCopied(true);
    toast.success('Student code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const streak = streakData.streak || {};
  const nextTier = streakData.next_tier;
  const progressToNext = nextTier ? Math.min(100, ((streak.current_streak || 0) / nextTier.days_required) * 100) : 100;

  return (
    <div className="min-h-screen pb-safe" style={{ background: '#FFF8F0' }}>
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold" style={{ color: '#1B4F8A' }}>My Profile</h1>
        <button data-testid="edit-profile-btn" onClick={() => editing ? handleSave() : setEditing(true)}
          className="px-4 py-2 rounded-full text-xs font-bold border-2"
          style={{ borderColor: '#E5DACE', color: '#1B4F8A' }}>
          {editing ? <><Check size={14} className="inline mr-1" />Save</> : <><Edit2 size={14} className="inline mr-1" />Edit</>}
        </button>
      </div>

      <div className="px-4 space-y-4">
        {/* Avatar & Name */}
        <div className="bg-white border-2 rounded-3xl p-5 text-center" style={{ borderColor: '#E5DACE', boxShadow: '0 4px 0 #E5DACE' }}>
          {editing ? (
            <div className="flex flex-wrap justify-center gap-2 mb-3">
              {AVATARS.map(a => (
                <button key={a} onClick={() => setForm(p => ({ ...p, avatar_url: a }))}
                  className={`text-3xl p-1 rounded-xl ${form.avatar_url === a ? 'bg-[#FFD23F]/30 ring-2 ring-[#FFD23F]' : ''}`}>{a}</button>
              ))}
            </div>
          ) : (
            <div className="text-5xl mb-2">{user?.avatar_url || '🧑‍🎓'}</div>
          )}
          {editing ? (
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="text-center text-xl font-extrabold bg-transparent border-b-2 outline-none w-full" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
          ) : (
            <p className="text-xl font-extrabold" style={{ color: '#1B4F8A' }}>{user?.name}</p>
          )}
          <p className="text-sm font-semibold mt-1" style={{ color: '#8B7355' }}>
            Class {profile?.class_level} · {profile?.board}
          </p>
        </div>

        {/* Streak Progress Card */}
        <div className="bg-gradient-to-br from-[#FF6B35] to-[#E63946] rounded-3xl p-4 text-white" data-testid="streak-progress-card"
          style={{ boxShadow: '0 4px 0 #B8291F' }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Flame size={22} className="text-white" style={{ animation: streak.current_streak > 0 ? 'flicker 1s ease-in-out infinite alternate' : 'none' }} />
              </div>
              <div>
                <p className="text-2xl font-black">{streak.current_streak || 0}</p>
                <p className="text-[10px] font-bold opacity-80">day streak</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold opacity-80">Best: {streak.longest_streak || 0} days</p>
              <p className="text-xs font-bold opacity-80">Total: {streak.total_days_practiced || 0} days</p>
            </div>
          </div>
          {nextTier && (
            <div>
              <div className="flex justify-between text-[10px] font-bold opacity-80 mb-1">
                <span>{streak.current_streak || 0} / {nextTier.days_required} days</span>
                <span>Next: {nextTier.label} (+{nextTier.coins_reward} coins)</span>
              </div>
              <div className="h-2.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-[#FFD23F] rounded-full transition-all duration-700"
                  style={{ width: `${progressToNext}%` }} />
              </div>
            </div>
          )}
          {!nextTier && streakData.current_tier && (
            <p className="text-xs font-bold opacity-80 text-center mt-1">Max tier reached!</p>
          )}
        </div>

        {/* Badges Grid */}
        <div className="bg-white border-2 rounded-3xl p-4" style={{ borderColor: '#E5DACE' }} data-testid="badges-section">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold flex items-center gap-2" style={{ color: '#1B4F8A' }}>
              <Trophy size={16} color="#FFD23F" /> My Badges
            </p>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFF0E5] text-[#FF6B35]">
              {badgesData.total_earned} earned
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {badgesData.badges.map((b, i) => {
              const IconComp = BADGE_ICONS[b.badge_icon] || Star;
              const color = BADGE_COLORS[b.badge_icon] || '#8B7355';
              const isEarned = b.status === 'earned';
              return (
                <div key={b.badge_name + i} data-testid={`badge-${b.badge_name?.replace(/\s/g, '-')}`}
                  className={`relative text-center p-3 rounded-2xl border-2 transition-all ${
                    isEarned ? 'border-[#FFD23F] bg-[#FFFBF0]' : 'border-[#E5DACE] bg-[#F8F4EF] opacity-60'}`}>
                  <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-1.5"
                    style={{ background: isEarned ? `${color}20` : '#E5DACE' }}>
                    {isEarned ? (
                      <IconComp size={18} color={color} />
                    ) : (
                      <Lock size={14} className="text-slate-400" />
                    )}
                  </div>
                  <p className="text-[10px] font-bold leading-tight" style={{ color: isEarned ? '#1B4F8A' : '#8B7355' }}>
                    {b.badge_name}
                  </p>
                  {!isEarned && b.days_required && (
                    <div className="mt-1.5">
                      <div className="h-1.5 bg-[#E5DACE] rounded-full overflow-hidden">
                        <div className="h-full bg-[#FF6B35] rounded-full" style={{ width: `${b.progress || 0}%` }} />
                      </div>
                      <p className="text-[8px] text-slate-400 font-bold mt-0.5">{b.days_required}d</p>
                    </div>
                  )}
                  {isEarned && b.coins_reward && (
                    <p className="text-[8px] font-bold text-[#FFD23F] mt-0.5">+{b.coins_reward}c</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Student Code */}
        <div className="bg-white border-2 rounded-2xl p-4 flex items-center justify-between" style={{ borderColor: '#E5DACE' }} data-testid="student-code-card">
          <div>
            <p className="text-xs font-bold" style={{ color: '#8B7355' }}>Student Code (Share with parent)</p>
            <p className="text-2xl font-black tracking-widest" style={{ color: '#FF6B35' }}>{profile?.student_code || '------'}</p>
          </div>
          <button data-testid="copy-code-btn" onClick={copyCode} className="p-2 rounded-full bg-[#F0E6D8]">
            {copied ? <Check size={18} color="#06D6A0" /> : <Copy size={18} color="#1B4F8A" />}
          </button>
        </div>

        {/* Plan Badge */}
        <div className="bg-white border-2 rounded-2xl p-4" style={{ borderColor: '#E5DACE' }}>
          <p className="text-xs font-bold mb-1" style={{ color: '#8B7355' }}>Current Plan</p>
          <span className="px-4 py-1.5 rounded-full text-sm font-bold"
            style={{ background: subscription?.plan === 'free' ? '#F0E6D8' : '#FFD23F', color: '#1B4F8A' }}>
            {(subscription?.plan || 'free').charAt(0).toUpperCase() + (subscription?.plan || 'free').slice(1)}
          </span>
        </div>

        {/* School & City editing */}
        {editing && (
          <div className="space-y-3 animate-slide-up">
            <input value={form.school_name} onChange={e => setForm(p => ({ ...p, school_name: e.target.value }))} placeholder="School Name"
              className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
            <input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} placeholder="City"
              className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
          </div>
        )}

        {/* Refer & Earn */}
        <button data-testid="referral-link" onClick={() => navigate('/referral')}
          className="w-full bg-gradient-to-r from-[#FF6B35] to-[#FFD23F] rounded-3xl p-4 flex items-center gap-3 border-0 text-left"
          style={{ boxShadow: '0 4px 0 #C04918' }}>
          <Gift size={24} color="white" />
          <div className="flex-1">
            <p className="text-white font-bold text-sm">Refer & Earn MathCoins</p>
            <p className="text-white/80 text-xs">Invite friends, earn 100 coins each!</p>
          </div>
        </button>

        {/* My Classes & Join Class */}
        <div className="bg-white border-2 rounded-3xl p-4" style={{ borderColor: '#E5DACE' }} data-testid="my-classes-section">
          <p className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1B4F8A' }}>
            <Users size={16} color="#8338EC" /> My Classes
          </p>
          {myClasses.length > 0 ? (
            <div className="space-y-2 mb-3">
              {myClasses.map(c => (
                <div key={c.id} className="flex items-center gap-2 p-2 rounded-xl" style={{ background: '#FFF8F0' }}>
                  <div className="w-8 h-8 rounded-full bg-[#8338EC]/10 flex items-center justify-center">
                    <Users size={14} color="#8338EC" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold" style={{ color: '#1B4F8A' }}>{c.name}</p>
                    <p className="text-[10px]" style={{ color: '#8B7355' }}>Class {c.class_level} · {c.teacher_name || 'Teacher'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-center py-2 mb-2" style={{ color: '#8B7355' }}>Not in any class yet. Join using a code from your teacher!</p>
          )}
          <div className="flex gap-2">
            <Input data-testid="join-class-input" value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())}
              placeholder="Enter class code (e.g., MTABC123)"
              className="flex-1 text-sm rounded-xl border-2 border-[#E5DACE] font-bold" />
            <Button data-testid="join-class-btn" onClick={handleJoinClass} disabled={joiningClass}
              className="bg-[#8338EC] text-white font-bold rounded-full px-4 text-xs">
              <Plus size={14} className="mr-1" /> Join
            </Button>
          </div>
        </div>

        {/* Logout */}
        <button data-testid="logout-btn" onClick={handleLogout}
          className="btn-press w-full py-4 rounded-full font-extrabold text-sm border-2 flex items-center justify-center gap-2"
          style={{ borderColor: '#EF4444', color: '#EF4444' }}>
          <LogOut size={16} /> Logout
        </button>
      </div>

      <style>{`
        @keyframes flicker {
          0% { opacity: 0.8; transform: scale(1); }
          100% { opacity: 1; transform: scale(1.1); }
        }
      `}</style>
      <BottomNav active="profile" />
    </div>
  );
}
